import { isDevMode } from '@angular/core';

import { DataState } from '../../model/data-state';
import { DataSourceContext } from '../../model/schema-context';
import { readValueAtKeys, SchemaAddress } from '../schema-address';
import { SchemaDataRead } from '../schema-resolver.types';

/**
 * What an address reaches from where it is resolved. Each member is read only by the root that needs it, so reading
 * `$data` tracks neither the context nor the source.
 */
export interface SchemaScope {
  /** What `$data` and `$state` read. */
  state(): DataState | undefined;

  /** What `$context` reads; `undefined` where no host published one. */
  context(): DataSourceContext | undefined;

  /** What `$source` reads: `undefined` where no producer is declared above, `null` while the nearest one connects. */
  source(): DataState | null | undefined;
}

/** One root of the vocabulary: how an address under it resolves, and which data it reads through. */
export interface SchemaRootResolver {
  resolve(address: SchemaAddress, scope: SchemaScope): unknown;

  dataOf?(keys: readonly string[]): SchemaDataRead | null;
}

/**
 * A key on one of the two envelopes, the node's state or its source's. The envelope is a fixed contract — `status`,
 * `data`, `error`, `messages`, plus what a source adds — so a key it does not carry is a mistake, not an absent value.
 */
export function readEnvelope(address: SchemaAddress, envelope: DataState): unknown {
  const [key, ...rest] = address.keys;

  if (!Object.hasOwn(envelope, key)) {
    reportMissingEnvelopeKey(address, key);

    return undefined;
  }

  return readValueAtKeys((envelope as unknown as Record<string, unknown>)[key], rest);
}

function reportMissingEnvelopeKey(address: SchemaAddress, key: string): void {
  if (isDevMode()) {
    console.warn(
      `Schema address "${address.text}": the $${address.root} envelope does not carry "${key}". It holds status, data, ` +
        `error and messages, plus whatever its source adds; a value in the data is $${address.root}.data.${key}.`
    );
  }
}
